import React, { useState, useMemo } from "react";
import VariantTableExtended from "../../../variants/VariantExplorerExtended/components/VariantTableExtended";
import { VARIANTS_PER_PAGE } from "../../../variants/VariantExplorer/constants";
import { generateActivityColorPalette } from "../../../variants/VariantExplorerExtended/utils/colorUtils";
import { getUniqueActivityCodes } from "../../../variants/VariantExplorerExtended/utils/activityUtils";

/**
 * Normalizes a variant list so each activity_sequence entry's `activity_code`
 * is the full activity name rather than the short code. Both the reference
 * and observed backends emit a short code (`activity_code`) plus a full name
 * (`activity` / `activity_name`); ActivityBlock renders whatever is in
 * `activity_code` and colors are hashed from that same string. Normalizing
 * both sides onto the full name keeps labels AND colors consistent between
 * the two panels — the short codes are generated independently per side and
 * aren't guaranteed to match for the same activity, but the full name always
 * is. CC-only: the standalone Variant Explorer Extended does not use this.
 */
const normalizeToFullName = (variants) => {
  return (variants || []).map((v) => ({
    ...v,
    activity_sequence: (v.activity_sequence || []).map((a) => ({
      ...a,
      activity_code: a.activity || a.activity_name || a.activity_code,
    })),
  }));
};

/**
 * Pagination controls (minimal, no external state needed here).
 */
const Pagination = ({ currentPage, totalPages, setCurrentPage }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-1 py-2 border-t border-gray-100">
      <button
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
        className="px-2 py-1 text-xs text-gray-500 border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50"
      >
        ‹
      </button>
      <span className="px-3 text-xs text-gray-600">
        {currentPage} / {totalPages}
      </span>
      <button
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
        className="px-2 py-1 text-xs text-gray-500 border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50"
      >
        ›
      </button>
    </div>
  );
};

/**
 * One self-contained variant panel (reference or observed side).
 * Manages its own pagination and sort state; selection is controlled externally.
 */
const VariantPanel = ({
  title,
  variants,
  selectedIds,
  onToggle,
  loading,
  colorPalette,
  activityCatalog,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState("trace_count");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleColumnSort = (key) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === variants.length) {
      variants.forEach((v) => {
        if (selectedIds.includes(v.variant_id)) onToggle(v.variant_id);
      });
    } else {
      variants.forEach((v) => {
        if (!selectedIds.includes(v.variant_id)) onToggle(v.variant_id);
      });
    }
  };

  const sortedVariants = useMemo(() => {
    return [...variants].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];
      if (sortKey === "variant_id") {
        return sortOrder === "asc"
          ? a.variant_id.localeCompare(b.variant_id)
          : b.variant_id.localeCompare(a.variant_id);
      }
      if (sortKey === "median_lead_time") {
        aVal = a.lead_time_stats?.median ?? 0;
        bVal = b.lead_time_stats?.median ?? 0;
      }
      aVal = aVal ?? 0;
      bVal = bVal ?? 0;
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [variants, sortKey, sortOrder]);

  const totalPages = Math.ceil(variants.length / VARIANTS_PER_PAGE);
  const currentVariants = sortedVariants.slice(
    (currentPage - 1) * VARIANTS_PER_PAGE,
    currentPage * VARIANTS_PER_PAGE
  );

  const unselected = variants.filter((v) => !selectedIds.includes(v.variant_id));
  const othersCases = unselected.reduce((sum, v) => sum + (v.trace_count ?? 0), 0);
  const totalCases = variants.reduce((sum, v) => sum + (v.trace_count ?? 0), 0);
  const others = {
    object_count: unselected.length === 0 ? "–" : othersCases,
    percentage: totalCases > 0 ? (othersCases / totalCases) * 100 : 0,
    num_steps: "–",
    median_lead_time: "–",
  };

  return (
    <div className="flex flex-col h-full min-h-0 border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50 flex-shrink-0">
        <span className="text-sm font-semibold text-gray-700">{title}</span>
        <span className="text-xs text-gray-500">
          {selectedIds.length} / {variants.length} selected
        </span>
      </div>
      <div className="flex-1 overflow-auto min-h-0">
        {loading ? (
          <div className="flex items-center justify-center h-32 text-sm text-gray-400">
            Loading variants…
          </div>
        ) : variants.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-gray-400">
            No variants found
          </div>
        ) : (
          <VariantTableExtended
            variants={variants}
            currentVariants={currentVariants}
            selectedVariants={selectedIds}
            handleToggleSelect={onToggle}
            toggleSelectAll={toggleSelectAll}
            sortKey={sortKey}
            sortOrder={sortOrder}
            handleColumnSort={handleColumnSort}
            loading={loading}
            others={others}
            colorPalette={colorPalette}
            activityCatalog={activityCatalog}
          />
        )}
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};

/**
 * Full-screen modal that lets the user select variants from the reference
 * and observed processes before running the visual conformance check.
 *
 * Props:
 *   referenceVariants       - variant list from /conformance/reference-variants
 *   referenceVariantsMeta   - { activity_catalog }
 *   selectedReferenceIds    - controlled selected IDs for reference side
 *   onToggleReference       - callback(variantId) to toggle reference selection
 *   observedHook            - object returned by useVariantExplorerExtended
 *   selectedObservedIds     - controlled selected IDs for observed side (from observedHook)
 *   onToggleObserved        - callback(variantId) to toggle observed selection
 *   onStart                 - called when the user clicks "Start Check"
 *   onCancel                - called when the user dismisses the modal
 *   loadingCheck            - true while the visual check is running
 */
const VariantSelectionModal = ({
  referenceVariants,
  referenceVariantsMeta,
  selectedReferenceIds,
  onToggleReference,
  observedHook,
  selectedObservedIds,
  onToggleObserved,
  onStart,
  onCancel,
  loadingCheck,
}) => {
  const referenceVariantsNormalized = useMemo(() => {
    return normalizeToFullName(referenceVariants);
  }, [referenceVariants]);

  const refColorPalette = useMemo(() => {
    const codes = getUniqueActivityCodes(referenceVariantsNormalized);
    return generateActivityColorPalette(codes);
  }, [referenceVariantsNormalized]);

  const observedVariantsNormalized = useMemo(() => {
    return normalizeToFullName(observedHook.variants);
  }, [observedHook.variants]);

  const obsColorPalette = useMemo(() => {
    const codes = getUniqueActivityCodes(observedVariantsNormalized);
    return generateActivityColorPalette(codes);
  }, [observedVariantsNormalized]);

  const canStart =
    selectedReferenceIds.length > 0 && selectedObservedIds.length > 0;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Instruction strip */}
      <div className="flex items-center justify-between px-6 py-2 border-b border-gray-100 bg-gray-50 flex-shrink-0">
        <p className="text-sm text-gray-500">
          Select at least one variant on each side, then click Start.
        </p>
        <button
          onClick={onCancel}
          className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2"
          aria-label="Back"
        >
          ← Back
        </button>
      </div>

      {/* Two-column variant panels */}
      <div className="flex-1 min-h-0 grid grid-cols-2 gap-4 p-4">
        <VariantPanel
          title="Reference Process"
          variants={referenceVariantsNormalized}
          selectedIds={selectedReferenceIds}
          onToggle={onToggleReference}
          loading={false}
          colorPalette={refColorPalette}
          activityCatalog={referenceVariantsMeta?.activity_catalog ?? {}}
        />
        <VariantPanel
          title="Observed Process"
          variants={observedVariantsNormalized}
          selectedIds={selectedObservedIds}
          onToggle={onToggleObserved}
          loading={observedHook.loading}
          colorPalette={obsColorPalette}
          activityCatalog={observedHook.activityCatalog ?? {}}
        />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
        <div className="flex items-center gap-6 text-sm text-gray-600">
          <span>
            Reference selected:{" "}
            <strong className="text-gray-900">{selectedReferenceIds.length}</strong>
          </span>
          <span>
            Observed selected:{" "}
            <strong className="text-gray-900">{selectedObservedIds.length}</strong>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onStart}
            disabled={!canStart || loadingCheck}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loadingCheck && (
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {loadingCheck ? "Running Check…" : "Start Check"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VariantSelectionModal;
